import React, { useState } from 'react';
import { fetchDonors, fetchLists, fetchNotes } from '../../lib/supabaseDb';
import { authPost } from '../../lib/authFetch';
import './MobileSettings.css';

function MobileSettings({ isOpen, onClose, user, subscription, onSignOut, userContext, onSaveUserContext }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  const [editingContext, setEditingContext] = useState(false);
  const [contextText, setContextText] = useState('');
  const [savingContext, setSavingContext] = useState(false);

  if (!isOpen) return null;

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
    setTouchCurrent(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setTouchCurrent(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging || !touchStart || !touchCurrent) {
      setIsDragging(false);
      return;
    }

    const dragDistance = touchCurrent - touchStart;
    if (dragDistance > 100) {
      onClose();
    }

    setIsDragging(false);
    setTouchStart(null);
    setTouchCurrent(null);
  };

  const dragOffset = isDragging && touchCurrent && touchStart
    ? Math.max(0, touchCurrent - touchStart)
    : 0;

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (subscription?.plan !== 'trial' || !subscription?.trial_ends_at) return null;
    const now = new Date();
    const trialEnd = new Date(subscription.trial_ends_at);
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  // Handle manage subscription
  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const response = await authPost('/api/create-portal-session', {});
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to open subscription portal:', error);
      alert('Failed to open subscription portal');
    }
    setManagingSubscription(false);
  };

  // Handle upgrade to Pro
  const handleUpgradeToPro = async () => {
    setUpgradingPlan(true);
    try {
      const response = await authPost('/api/create-checkout-session', {});
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
      alert('Failed to start checkout. Please try again.');
    }
    setUpgradingPlan(false);
  };

  // Handle edit context
  const handleEditContext = () => {
    setContextText(userContext || '');
    setEditingContext(true);
  };

  // Handle save context
  const handleSaveContext = async () => {
    setSavingContext(true);
    try {
      await onSaveUserContext?.(contextText);
      setEditingContext(false);
    } catch (error) {
      console.error('Failed to save context:', error);
      alert('Failed to save. Please try again.');
    }
    setSavingContext(false);
  };

  // Handle export
  const handleExport = async (format = 'csv') => {
    if (!user?.id) return;
    setExporting(true);
    try {
      const donors = await fetchDonors(user.id);
      const lists = await fetchLists(user.id);
      const notes = await fetchNotes(user.id);

      if (format === 'csv') {
        const headers = ['Name', 'Stage', 'Amount', 'Last Contact', 'Narrative'];
        const csvRows = [headers.join(',')];

        donors.forEach(donor => {
          const row = [
            `"${(donor.name || '').replace(/"/g, '""')}"`,
            `"${(donor.stage || '').replace(/"/g, '""')}"`,
            `"${(donor.amount || '').replace(/"/g, '""')}"`,
            `"${(donor.last_contact || '').replace(/"/g, '""')}"`,
            `"${(donor.narrative || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
          ];
          csvRows.push(row.join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `knowroom-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const exportData = { donors, lists, notes, exportedAt: new Date().toISOString() };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `knowroom-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed');
    }
    setExporting(false);
  };

  const handleLogout = () => {
    onSignOut();
    onClose();
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="mobile-settings"
        style={{ transform: `translateY(${dragOffset}px)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div
          className="settings-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="settings-handle-bar" />
        </div>

        <div className="settings-content">
          <h2 className="settings-title">Settings</h2>

          {/* Account section */}
          <div className="settings-section">
            <div className="settings-label">Account</div>
            <div className="settings-value">{user?.email || 'Not signed in'}</div>
          </div>

          {/* Plan section */}
          {subscription && (
            <div className="settings-section">
              <div className="settings-label">Plan</div>
              <div className="plan-row">
                <span className={`plan-badge plan-${subscription.plan}`}>
                  {subscription.plan === 'trial' ? 'Trial' : subscription.plan === 'pro' ? 'Pro' : 'Free'}
                </span>
                <span className="plan-status">
                  {subscription.plan === 'trial' && trialDaysRemaining !== null && (
                    trialDaysRemaining > 0
                      ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} left`
                      : 'Trial expired'
                  )}
                  {subscription.plan === 'pro' && subscription.current_period_end && (
                    subscription.cancel_at_period_end
                      ? `Ends ${formatDate(subscription.current_period_end)}`
                      : `Renews ${formatDate(subscription.current_period_end)}`
                  )}
                  {subscription.plan === 'free' && (
                    `${subscription.donor_limit || 20} people limit`
                  )}
                </span>
              </div>
              {/* Upgrade button for trial/free users */}
              {(subscription.plan === 'trial' || subscription.plan === 'free') && (
                <button
                  className="settings-action-btn upgrade-btn"
                  onClick={handleUpgradeToPro}
                  disabled={upgradingPlan}
                >
                  {upgradingPlan ? 'Loading...' : 'Upgrade to Pro'}
                </button>
              )}
              {/* Manage subscription for pro users */}
              {subscription.plan === 'pro' && subscription.stripe_customer_id && (
                <button
                  className="settings-action-btn"
                  onClick={handleManageSubscription}
                  disabled={managingSubscription}
                >
                  {managingSubscription ? 'Loading...' : 'Manage Subscription'}
                </button>
              )}
            </div>
          )}

          {/* User Context section */}
          <div className="settings-section">
            <div className="settings-label">Your Context</div>
            <p className="settings-description">
              Help the AI understand how you use Knowroom for better responses.
            </p>
            {editingContext ? (
              <div className="context-edit">
                <textarea
                  className="context-textarea"
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                  placeholder="e.g., I'm a nonprofit fundraiser focused on major gifts..."
                  rows={4}
                />
                <div className="context-buttons">
                  <button
                    className="context-btn cancel"
                    onClick={() => setEditingContext(false)}
                    disabled={savingContext}
                  >
                    Cancel
                  </button>
                  <button
                    className="context-btn save"
                    onClick={handleSaveContext}
                    disabled={savingContext}
                  >
                    {savingContext ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {userContext && (
                  <p className="context-preview">{userContext}</p>
                )}
                <button className="settings-action-btn" onClick={handleEditContext}>
                  {userContext ? 'Edit Context' : 'Set Context'}
                </button>
              </>
            )}
          </div>

          {/* Export section */}
          <div className="settings-section">
            <div className="settings-label">Export Data</div>
            <div className="export-row">
              <button
                className="export-btn"
                onClick={() => handleExport('csv')}
                disabled={exporting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                CSV
              </button>
              <button
                className="export-btn"
                onClick={() => handleExport('json')}
                disabled={exporting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                JSON
              </button>
            </div>
          </div>

          {/* Logout button */}
          <button className="settings-logout" onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>

          {/* Legal */}
          <div className="settings-legal">
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
            <span>•</span>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileSettings;
